import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import { useGetGameConfigQuery } from "@store/avatarApi";
import {
  AvatarType,
  AzureAvatarConfig,
  HeygenAvatarConfig,
  type AvatarConfig,
} from "@models/avatar-config";
import { AzureAvatarService } from "@service/avatar/azure-avatar.service";
import { HeygenAvatarService } from "@service/avatar/heygen-avatar.service";

/**
 * Imperative handle exposed by the in-game voice-over overlay. The hosting
 * game component grabs a ref and calls `speak(text)` on round/item changes.
 *
 * Hosts can choose interruption behavior per call. This supports true queue
 * semantics (finish current speech before next) while still allowing explicit
 * interrupts when game state changes.
 */
export interface GameAvatarOverlayHandle {
  /**
   * Narrate `text` through the avatar.
   *
   * Uses a FIFO queue: each utterance is spoken in order and must complete
   * before the next starts. If called before provider startup finishes, the
   * text is queued and automatically drained when ready.
   *
   * Use `stop()` when you explicitly need to interrupt/cancel in-flight speech
   * (e.g. game step advanced and pending narration is stale).
   */
  speak: (
    text: string,
    options?: { interrupt?: boolean; replaceQueue?: boolean }
  ) => void;
  /** Stop the current utterance without tearing down the streaming session. */
  stop: () => void;
}

export interface GameAvatarOverlayProps {
  /** Active game-session id (rows in `game_session`, not the companion `session` table). */
  gameSessionId: string;
  /**
   * When `true`, the overlay renders, fetches /avatar/game-config, and
   * starts the provider streaming session. When `false`, the overlay is
   * unmounted and zero streaming cost is incurred.
   */
  enabled: boolean;
  /**
   * When `true`, the avatar is muted: any in-flight utterance is
   * interrupted and new `speak()` calls become no-ops until the flag is
   * cleared. The streaming session itself stays up so unmuting feels
   * instant.
   */
  muted?: boolean;
  /** Notifies parent when provider session becomes ready/unready. */
  onReadyChange?: (ready: boolean) => void;
}

/** Keep aligned with gallery avatar container sizing. */
const OverlayContainer = styled.div`
  position: absolute;
  bottom: 96px;
  right: 4px;
  width: clamp(120px, 20vw, 225px);
  aspect-ratio: 3 / 4;
  height: auto;
  max-width: 225px;
  max-height: 300px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  pointer-events: none;
  z-index: 1200;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  background: rgba(0, 0, 0, 0.04);
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
`;

const HiddenAudio = styled.audio`
  display: none;
`;

/**
 * Speak-only avatar overlay used by selection / categorization games to
 * narrate round prompts and per-item descriptions. Phase 1 of the
 * voice-over feature.
 *
 * This component intentionally does not include microphone capture, VAD,
 * or any conversation-pacing logic — that lives in the companion
 * `<Avatar />`. Phase 2 (voice-driven gameplay) will add a sibling
 * `GameVoiceInput` component that shares the same provider session.
 */
const GameAvatarOverlayInner = forwardRef<
  GameAvatarOverlayHandle,
  GameAvatarOverlayProps
>(({ gameSessionId, muted = false, onReadyChange }, ref) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const azureServiceRef = useRef<AzureAvatarService | null>(null);
  const heygenServiceRef = useRef<HeygenAvatarService | null>(null);
  const queueRef = useRef<string[]>([]);
  const speakingRef = useRef(false);
  const mountedRef = useRef(true);
  const [ready, setReady] = useState(false);
  const readyRef = useRef(false);
  readyRef.current = ready;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const { data: avatarConfig } = useGetGameConfigQuery(gameSessionId, {
    skip: !gameSessionId,
  });

  // Keep the latest config in a ref so the start/stop effect can read it
  // without re-running on every refetch.
  const configRef = useRef<AvatarConfig | undefined>(avatarConfig);
  configRef.current = avatarConfig;

  const stopCurrent = useMemo(
    () => async () => {
      try {
        if (azureServiceRef.current) {
          await azureServiceRef.current.stopSpeaking();
        }
        if (heygenServiceRef.current) {
          await heygenServiceRef.current.stopSpeaking();
        }
      } catch {
        // best-effort: ignore provider errors here, the next speak() will
        // either succeed or surface its own failure
      }
    },
    [],
  );

  // Drains the speak queue serially. Earlier utterances are interrupted
  // before the next one starts so narration never piles up behind a
  // stale prompt the player has already moved past.
  const drainQueue = useMemo(
    () => async () => {
      if (speakingRef.current) return;
      if (!readyRef.current) return;
      speakingRef.current = true;
      try {
        while (queueRef.current.length > 0 && !mutedRef.current) {
          const next = queueRef.current.shift();
          if (!next) continue;
          if (azureServiceRef.current) {
            await azureServiceRef.current.speak(next);
          } else if (heygenServiceRef.current) {
            await heygenServiceRef.current.speak(next);
          }
          if (!mountedRef.current) return;
        }
        // Anything left in the queue (because we got muted mid-drain) is
        // dropped: by the time the user unmutes, the visible game state has
        // moved on and the queued text would no longer be relevant.
        if (mutedRef.current) {
          queueRef.current = [];
        }
      } finally {
        speakingRef.current = false;
      }
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      speak: (
        text: string,
        options?: { interrupt?: boolean; replaceQueue?: boolean }
      ) => {
        const trimmed = (text ?? "").trim();
        if (!trimmed) return;
        if (mutedRef.current) return;
        const interrupt = options?.interrupt ?? true;
        const replaceQueue = options?.replaceQueue ?? true;
        if (replaceQueue) {
          queueRef.current = [trimmed];
        } else {
          queueRef.current.push(trimmed);
        }
        if (!readyRef.current) return;
        if (interrupt) {
          void stopCurrent().then(() => {
            void drainQueue();
          });
        } else {
          void drainQueue();
        }
      },
      stop: () => {
        queueRef.current = [];
        void stopCurrent();
      },
    }),
    [stopCurrent, drainQueue],
  );

  // Mute side-effects: tear down whatever the avatar is saying right now
  // and prevent the queue from feeding more text until unmuted.
  useEffect(() => {
    if (muted) {
      queueRef.current = [];
      void stopCurrent();
    }
  }, [muted, stopCurrent]);

  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  // If a `speak()` call happened before the session became ready, flush the
  // queued utterance once provider startup completes.
  useEffect(() => {
    if (!ready || mutedRef.current || queueRef.current.length === 0) return;
    void drainQueue();
  }, [ready, drainQueue]);

  // Lifecycle: starts the provider session as soon as we have a config,
  // tears it down on unmount or when `enabled` flips to false (the parent
  // unmounts the component in that case).
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    const start = async () => {
      const config = configRef.current;
      if (!config) return;
      if (config.type === AvatarType.Azure) {
        const azure = new AzureAvatarService();
        azureServiceRef.current = azure;
        try {
          await azure.start(
            config as AzureAvatarConfig,
            videoRef.current!,
            audioRef.current!,
            gameSessionId,
          );
          if (!cancelled && azure.running && !azure.stopping) {
            setReady(true);
          }
        } catch {
          // Provider failed to start — leave the overlay hidden by keeping
          // `ready` false. The mute/speak APIs become no-ops naturally.
        }
      } else if (config.type === AvatarType.Heygen) {
        const heygen = new HeygenAvatarService();
        heygenServiceRef.current = heygen;
        try {
          await heygen.start(config as HeygenAvatarConfig, () => {});
          if (!cancelled && heygen.running && !heygen.stopping) {
            // HeyGen exposes its own MediaStream; pipe it into the video tag.
            if (videoRef.current && heygen.stream) {
              videoRef.current.srcObject = heygen.stream;
            }
            setReady(true);
          }
        } catch {
          // see above — fail soft
        }
      } else {
        // Audio provider is currently stubbed; treat as no-op.
      }
    };
    if (avatarConfig) {
      void start();
    }
    return () => {
      cancelled = true;
      mountedRef.current = false;
      setReady(false);
      onReadyChange?.(false);
      const azure = azureServiceRef.current;
      const heygen = heygenServiceRef.current;
      azureServiceRef.current = null;
      heygenServiceRef.current = null;
      queueRef.current = [];
      if (azure) {
        void azure.stop();
      }
      if (heygen) {
        void heygen.stop();
      }
    };
  }, [avatarConfig, gameSessionId]);

  if (!avatarConfig) {
    return null;
  }

  return (
    <OverlayContainer aria-hidden="true">
      <Video autoPlay playsInline muted={muted} ref={videoRef} />
      <HiddenAudio autoPlay playsInline ref={audioRef} />
    </OverlayContainer>
  );
});

GameAvatarOverlayInner.displayName = "GameAvatarOverlayInner";

/**
 * Public wrapper that lazily mounts `GameAvatarOverlayInner` only when
 * `enabled` is true. While disabled, no `/avatar/game-config` request is
 * made and no provider streaming session is opened, so the feature is
 * truly free for users who never enable the toggle.
 */
const GameAvatarOverlay = forwardRef<
  GameAvatarOverlayHandle,
  GameAvatarOverlayProps
>((props, ref) => {
  if (!props.enabled || !props.gameSessionId) {
    return null;
  }
  return <GameAvatarOverlayInner ref={ref} {...props} />;
});

GameAvatarOverlay.displayName = "GameAvatarOverlay";

export default GameAvatarOverlay;
